/**
 * Sprint v1.7 Phase 4 (Do 03.09.2026) — Phase-Anchor + Step-Header.
 *
 * Wrapper-Component für eine Cockpit-Phase (Mein Muster / Andere verstehen /
 * Wirkung gestalten). Rendert:
 *   - Anchor-ID (für Sidebar-Scroll)
 *   - Step-Nummerierung "Schritt X von 3"
 *   - Progress-Bar (visueller Gesamtfortschritt)
 *   - Phasen-Header (Eyebrow + H2 + Beschreibung)
 *   - Kinder (bestehende Cockpit-Inhalte)
 *   - "Was kommt als Nächstes?"-Teaser (verlinkt auf nächste Phase)
 *
 * Angelehnt an MarketingSecrets.ai-Pattern für User-Orientierung.
 * Sprint-Auftrag Do 03.09.2026 12:43 · Option Gamma.
 */
import React from "react";
import { ArrowRight } from "lucide-react";

const PHASEN_TOTAL = 3;

const NEXT_PHASE = {
  1: {
    label: "Andere verstehen",
    anchor: "phase-andere-verstehen",
    teaser: "Fokustyp-Radar + Zugangscode: So liest du dein Gegenüber.",
  },
  2: {
    label: "Wirkung gestalten",
    anchor: "phase-wirkung-gestalten",
    teaser: "Türöffner + Kommunikationspaket: So öffnest du die Tür.",
  },
  3: null,
};

export default function PhaseAnchor({
  step,
  id,
  eyebrow,
  title,
  description,
  children,
}) {
  const next = NEXT_PHASE[step];
  const progressPct = Math.round((step / PHASEN_TOTAL) * 100);

  const handleNext = (e) => {
    if (!next) return;
    e.preventDefault();
    const el = document.getElementById(next.anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id={id}
      className="scroll-mt-24 space-y-5"
      data-testid={`phase-anchor-${step}`}
      aria-labelledby={`${id}-title`}
    >
      {/* Step-Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--fts-primary-bright)]/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]"
            data-testid={`phase-step-badge-${step}`}
          >
            Schritt {step} von {PHASEN_TOTAL}
          </span>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
            {eyebrow}
          </span>
        </div>

        {/* Progress-Bar */}
        <div
          className="h-1.5 w-full max-w-xs rounded-full bg-slate-200 overflow-hidden"
          data-testid={`phase-progress-${step}`}
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Fortschritt: Schritt ${step} von ${PHASEN_TOTAL}`}
        >
          <div
            className="h-full rounded-full bg-[var(--fts-primary-bright)] transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div>
          <h2
            id={`${id}-title`}
            data-testid={`phase-title-${step}`}
            className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
          >
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-slate-600 max-w-xl">{description}</p>
          )}
        </div>
      </div>

      {/* Phase-Inhalt */}
      <div className="space-y-4">{children}</div>

      {/* Was kommt als Nächstes? Teaser */}
      {next && (
        <a
          href={`#${next.anchor}`}
          onClick={handleNext}
          data-testid={`phase-next-teaser-${step}`}
          className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white hover:border-[var(--fts-primary-bright)]/40 hover:shadow-sm p-4 transition-all"
        >
          <div className="w-9 h-9 rounded-lg bg-[var(--fts-primary-bright)]/10 text-[var(--fts-primary-bright)] flex items-center justify-center flex-shrink-0">
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Was kommt als Nächstes
            </p>
            <p className="mt-0.5 text-sm font-bold text-slate-900">
              Schritt {step + 1}: {next.label}
            </p>
            <p className="mt-0.5 text-xs text-slate-600 leading-snug">
              {next.teaser}
            </p>
          </div>
        </a>
      )}
    </section>
  );
}
